// src/components/pages/allocation/partial/extendedDescriptions.js

import React from 'react';
import Latex from '@matejmazur/react-katex';
import 'katex/dist/katex.min.css';
import { useTranslation, Trans } from 'react-i18next';

const useExtendedDescriptions = () => {
  const { t } = useTranslation();

  const extendedDescriptions = {
    minVar: (
      <div>
        <h3>
          <Trans i18nKey="extendedDescriptions.minVar.title" components={{ bold: <b /> }} />
        </h3>
        <p>
          <Trans i18nKey="extendedDescriptions.minVar.p1" components={{ bold: <b /> }} />
        </p>
        <p>
          <Trans i18nKey="extendedDescriptions.minVar.p2" components={{ bold: <b /> }} />
        </p>

        <h3>
          <Trans i18nKey="extendedDescriptions.minVar.visualTitle" components={{ bold: <b /> }} />
        </h3>
        <p>
          <Trans i18nKey="extendedDescriptions.minVar.visualDesc" components={{ bold: <b /> }} />
        </p>
        <ul>
          <li>
            <Trans
              i18nKey="extendedDescriptions.minVar.bullet1"
              components={{ bold: <b /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="extendedDescriptions.minVar.bullet2"
              components={{ bold: <b /> }}
            />
          </li>
        </ul>

        <img
          src="/modelexplanation/MinVar_GLD_SPY.png"
          alt={t('extendedDescriptions.minVar.altImg')}
          style={{ maxWidth: '100%', height: 'auto', marginTop: '20px', marginBottom: '20px' }}
        />

        <h3>
          <Trans i18nKey="extendedDescriptions.minVar.mathTitle" components={{ bold: <b /> }} />
        </h3>
        <p>
          <Trans i18nKey="extendedDescriptions.minVar.mathP1" components={{ bold: <b /> }} />
          <Latex>{`E(R_p)`}</Latex>
          <Trans i18nKey="extendedDescriptions.minVar.mathP2" components={{ bold: <b /> }} />
          <Latex>{`\\sigma_p^2`}</Latex>
          <Trans i18nKey="extendedDescriptions.minVar.mathP3" components={{ bold: <b /> }} />
        </p>

        <h4>
          <Trans i18nKey="extendedDescriptions.minVar.returnTitle" components={{ bold: <b /> }} />
        </h4>
        <Latex block>{`E(R_p) = \\sum_{i=1}^{n} w_i E(R_i)`}</Latex>

        <h4>
          <Trans i18nKey="extendedDescriptions.minVar.varianceTitle" components={{ bold: <b /> }} />
        </h4>
        <Latex block>{`\\sigma_p^2 = \\sum_{i=1}^{n} \\sum_{j=1}^{n} w_i w_j \\sigma_{ij}`}</Latex>

        <h4>
          <Trans i18nKey="extendedDescriptions.minVar.objectiveTitle" components={{ bold: <b /> }} />
        </h4>
        <Latex block>{`\\min_w (w^T \\Sigma w)`}</Latex>

        <p>
          <Trans i18nKey="extendedDescriptions.minVar.subjectTo" components={{ bold: <b /> }} />
        </p>
        <Latex block>{`\\sum_{i=1}^{n} w_i = 1`}</Latex>
      </div>
    ),

    riskParity: (
      <div>
        <h3>
          <Trans i18nKey="extendedDescriptions.riskParity.title" components={{ bold: <b /> }} />
        </h3>
        <p>
          <Trans i18nKey="extendedDescriptions.riskParity.p1" components={{ bold: <b /> }} />
        </p>
        <p>
          <Trans i18nKey="extendedDescriptions.riskParity.p2" components={{ bold: <b /> }} />
        </p>

        <h3>
          <Trans i18nKey="extendedDescriptions.riskParity.conceptTitle" components={{ bold: <b /> }} />
        </h3>
        <ul>
          <li>
            <Trans i18nKey="extendedDescriptions.riskParity.bullet1" components={{ bold: <b /> }} />
          </li>
          <li>
            <Trans i18nKey="extendedDescriptions.riskParity.bullet2" components={{ bold: <b /> }} />
          </li>
          <li>
            <Trans i18nKey="extendedDescriptions.riskParity.bullet3" components={{ bold: <b /> }} />
          </li>
        </ul>

        <h3>
          <Trans i18nKey="extendedDescriptions.riskParity.mathTitle" components={{ bold: <b /> }} />
        </h3>

        <h4>
          <Trans i18nKey="extendedDescriptions.riskParity.rcTitle" components={{ bold: <b /> }} />
        </h4>
        <p>
          <Trans i18nKey="extendedDescriptions.riskParity.rcP1" components={{ bold: <b /> }} />
          <Latex>{`i`}</Latex>
          <Trans i18nKey="extendedDescriptions.riskParity.rcP2" components={{ bold: <b /> }} />
          <Latex>{`RC_i`}</Latex>
          <Trans i18nKey="extendedDescriptions.riskParity.rcP3" components={{ bold: <b /> }} />
        </p>
        <Latex block>{`RC_i = w_i \\cdot (\\Sigma w)_i`}</Latex>
        <p>
          <Trans i18nKey="extendedDescriptions.riskParity.rcP4" components={{ bold: <b /> }} />
          <Latex>{`RC_i`}</Latex>
          <Trans i18nKey="extendedDescriptions.riskParity.rcP5" components={{ bold: <b /> }} />
          <Latex>{`\\sigma_p`}</Latex>
          <Trans i18nKey="extendedDescriptions.riskParity.rcP6" components={{ bold: <b /> }} />
        </p>
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <Latex block>{`\\text{Risk Contribution Ratio}_i = \\frac{w_i \\cdot (\\Sigma w)_i}{\\sigma_p}`}</Latex>
        </div>
        <p>
          <Trans i18nKey="extendedDescriptions.riskParity.rcP7" components={{ bold: <b /> }} />
        </p>
        <ul>
          <li>
            <Latex>{`w_i`}</Latex>: <Trans i18nKey="extendedDescriptions.riskParity.rcList1" components={{ bold: <b /> }} />
          </li>
          <li>
            <Latex>{`\\Sigma`}</Latex>: <Trans i18nKey="extendedDescriptions.riskParity.rcList2" components={{ bold: <b /> }} />
          </li>
          <li>
            <Latex>{`(\\Sigma w)_i`}</Latex>: <Trans i18nKey="extendedDescriptions.riskParity.rcList3" components={{ bold: <b /> }} />
          </li>
          <li>
            <Latex>{`\\sigma_p`}</Latex>: <Trans i18nKey="extendedDescriptions.riskParity.rcList4" components={{ bold: <b /> }} />
          </li>
        </ul>

        <h4>
          <Trans i18nKey="extendedDescriptions.riskParity.conditionTitle" components={{ bold: <b /> }} />
        </h4>
        <p>
          <Trans i18nKey="extendedDescriptions.riskParity.conditionP1" components={{ bold: <b /> }} />
        </p>
        <Latex block>{`RC_1 = RC_2 = ... = RC_n`}</Latex>
        <p>
          <Trans i18nKey="extendedDescriptions.riskParity.conditionP2" components={{ bold: <b /> }} />
        </p>
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <Latex block>{`\\frac{w_1 \\cdot (\\Sigma w)_1}{\\sigma_p} = \\frac{w_2 \\cdot (\\Sigma w)_2}{\\sigma_p} = \\dots = \\frac{w_n \\cdot (\\Sigma w)_n}{\\sigma_p} = \\frac{1}{n}`}</Latex>
        </div>

        <h4>
          <Trans i18nKey="extendedDescriptions.riskParity.optimalTitle" components={{ bold: <b /> }} />
        </h4>
        <p>
          <Trans i18nKey="extendedDescriptions.riskParity.optimalP1" components={{ bold: <b /> }} />
          <Latex>{`w^*`}</Latex>
          <Trans i18nKey="extendedDescriptions.riskParity.optimalP2" components={{ bold: <b /> }} />
        </p>
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <Latex block>{`
          \\min_w \\sum_{i=1}^{n} \\left( \\frac{w_i (\\Sigma w)_i}{\\sqrt{w^T \\Sigma w}} - \\frac{1}{n} \\right)^2
          `}</Latex>
        </div>
        <p>
          <Trans i18nKey="extendedDescriptions.riskParity.subjectTo" components={{ bold: <b /> }} />
        </p>
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <Latex block>{`\\sum_{i=1}^{n} w_i = 1, \\quad w_i > 0`}</Latex>
        </div>
        <p>
          <Trans i18nKey="extendedDescriptions.riskParity.rcP8" components={{ bold: <b /> }} />
          <Latex>{`\\sqrt{w^T \\Sigma w}`}</Latex>
          <Trans i18nKey="extendedDescriptions.riskParity.rcP9" components={{ bold: <b /> }} />
          <Latex>{`\\sigma_p`}</Latex>
          <Trans i18nKey="extendedDescriptions.riskParity.rcP10" components={{ bold: <b /> }} />
        </p>

        <h3>
          <Trans i18nKey="extendedDescriptions.riskParity.advantagesTitle" components={{ bold: <b /> }} />
        </h3>
        <ul>
          <li>
            <Trans i18nKey="extendedDescriptions.riskParity.advantages.0" components={{ bold: <b /> }} />
          </li>
          <li>
            <Trans i18nKey="extendedDescriptions.riskParity.advantages.1" components={{ bold: <b /> }} />
          </li>
          <li>
            <Trans i18nKey="extendedDescriptions.riskParity.advantages.2" components={{ bold: <b /> }} />
          </li>
        </ul>
      </div>
    ),

    maxSharpe: (
      <div>
        <h3>
          <Trans i18nKey="extendedDescriptions.maxSharpe.title" components={{ bold: <b /> }} />
        </h3>
        <p>
          <Trans i18nKey="extendedDescriptions.maxSharpe.p1" components={{ bold: <b /> }} />
        </p>
        <p>
          <Trans i18nKey="extendedDescriptions.maxSharpe.p2" components={{ bold: <b /> }} />
        </p>

        <h3>
          <Trans i18nKey="extendedDescriptions.maxSharpe.visualTitle" components={{ bold: <b /> }} />
        </h3>
        <p>
          <Trans i18nKey="extendedDescriptions.maxSharpe.visualDesc" components={{ bold: <b /> }} />
        </p>
        <ul>
          <li>
            <Trans i18nKey="extendedDescriptions.maxSharpe.bullet1" components={{ bold: <b /> }} />
          </li>
          <li>
            <Trans i18nKey="extendedDescriptions.maxSharpe.bullet2" components={{ bold: <b /> }} />
          </li>
        </ul>
        <img
          src="/modelexplanation/MaxSharpe_GLD_SPY.png"
          alt={t('extendedDescriptions.maxSharpe.altImg')}
          style={{ maxWidth: '100%', height: 'auto', marginTop: '20px', marginBottom: '20px' }}
        />

        <h3>
          <Trans i18nKey="extendedDescriptions.maxSharpe.mathTitle" components={{ bold: <b /> }} />
        </h3>
        <h4>
          <Trans i18nKey="extendedDescriptions.maxSharpe.sharpeFormulaTitle" components={{ bold: <b /> }} />
        </h4>
        <Latex block>{`S(w) = \\frac{E(R_p) - R_f}{\\sigma_p}`}</Latex>
        <p>
          <Trans i18nKey="extendedDescriptions.maxSharpe.sharpeFormulaDesc" components={{ bold: <b /> }} />
        </p>
        <ul>
          <li>
            <Latex>{`E(R_p)`}</Latex>: <Trans i18nKey="extendedDescriptions.maxSharpe.rcList1" components={{ bold: <b /> }} />
          </li>
          <li>
            <Latex>{`R_f`}</Latex>: <Trans i18nKey="extendedDescriptions.maxSharpe.rcList2" components={{ bold: <b /> }} />
          </li>
          <li>
            <Latex>{`\\sigma_p`}</Latex>: <Trans i18nKey="extendedDescriptions.maxSharpe.rcList3" components={{ bold: <b /> }} />
          </li>
        </ul>

        <h4>
          <Trans i18nKey="extendedDescriptions.maxSharpe.objectiveTitle" components={{ bold: <b /> }} />
        </h4>
        <Latex block>{`\\max_w \\frac{w^T \\mu - R_f}{\\sqrt{w^T \\Sigma w}}`}</Latex>
        <p>
          <Trans i18nKey="extendedDescriptions.maxSharpe.subjectTo" components={{ bold: <b /> }} />
        </p>
        <Latex block>{`\\sum_{i=1}^{n} w_i = 1`}</Latex>
      </div>
    ),

    blackLiterman: (
      <div>
        <h3>
          <Trans i18nKey="extendedDescriptions.blackLiterman.title" components={{ bold: <b /> }} />
        </h3>
        <p>
          <Trans i18nKey="extendedDescriptions.blackLiterman.p1" components={{ bold: <b /> }} />
        </p>
        <p>
          <Trans i18nKey="extendedDescriptions.blackLiterman.p2" components={{ bold: <b /> }} />
        </p>

        <h3>
          <Trans i18nKey="extendedDescriptions.blackLiterman.marketTitle" components={{ bold: <b /> }} />
        </h3>
        <p>
          <Trans i18nKey="extendedDescriptions.blackLiterman.marketDesc" components={{ bold: <b /> }} />
          {/* <Latex>{`\pi`}</Latex> */}
          <Trans i18nKey="extendedDescriptions.blackLiterman.afterPi" components={{ bold: <b /> }} />
        </p>
        <Latex block>{`\pi = \lambda \Sigma w_{mkt}`}</Latex>
        <p>
          <Trans i18nKey="extendedDescriptions.blackLiterman.marketWhere" components={{ bold: <b /> }} />
        </p>
        <ul>
          <li>
            <Latex>{`\lambda`}</Latex>: <Trans i18nKey="extendedDescriptions.blackLiterman.lambdaDesc" components={{ bold: <b /> }} />
          </li>
          <li>
            <Latex>{`\Sigma`}</Latex>: <Trans i18nKey="extendedDescriptions.blackLiterman.sigmaDesc" components={{ bold: <b /> }} />
          </li>
          <li>
            <Latex>{`w_{mkt}`}</Latex>: <Trans i18nKey="extendedDescriptions.blackLiterman.wmktDesc" components={{ bold: <b /> }} />
          </li>
        </ul>

        <h3>
          <Trans i18nKey="extendedDescriptions.blackLiterman.viewsTitle" components={{ bold: <b /> }} />
        </h3>
        <p>
          <Trans i18nKey="extendedDescriptions.blackLiterman.viewsDesc" components={{ bold: <b /> }} />
        </p>
        <p>
          <Trans i18nKey="extendedDescriptions.blackLiterman.blpExpectedReturn" components={{ bold: <b /> }} />
          <Latex>{`E[R]`}</Latex>
          <Trans i18nKey="extendedDescriptions.blackLiterman.blpExpectedReturnAfter" components={{ bold: <b /> }} />
        </p>

        <h3>
          <Trans i18nKey="extendedDescriptions.blackLiterman.bayesianTitle" components={{ bold: <b /> }} />
        </h3>
        <p>
          <Trans i18nKey="extendedDescriptions.blackLiterman.bayesianDesc" components={{ bold: <b /> }} />
        </p>

        <h3>
          <Trans i18nKey="extendedDescriptions.blackLiterman.mathTitle" components={{ bold: <b /> }} />
        </h3>
        <h4>
          <Trans i18nKey="extendedDescriptions.blackLiterman.expectedReturnTitle" components={{ bold: <b /> }} />
        </h4>
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <Latex block>{`E[R] = \\left( (\\tau \\Sigma)^{-1} + P^T \\Omega^{-1} P \\right)^{-1} \\left( (\\tau \\Sigma)^{-1} \\pi + P^T \\Omega^{-1} Q \\right)`}</Latex>
        </div>
        <p>
          <Trans i18nKey="extendedDescriptions.blackLiterman.afterExpectedReturn" components={{ bold: <b /> }} />
        </p>
        <ul>
          <li>
            <Latex>{`\tau`}</Latex>: <Trans i18nKey="extendedDescriptions.blackLiterman.tauDesc" components={{ bold: <b /> }} />
          </li>
          <li>
            <Latex>{`P`}</Latex>: <Trans i18nKey="extendedDescriptions.blackLiterman.PDesc" components={{ bold: <b /> }} />
          </li>
          <li>
            <Latex>{`\Omega`}</Latex>: <Trans i18nKey="extendedDescriptions.blackLiterman.omegaDesc" components={{ bold: <b /> }} />
          </li>
          <li>
            <Latex>{`Q`}</Latex>: <Trans i18nKey="extendedDescriptions.blackLiterman.QDesc" components={{ bold: <b /> }} />
          </li>
        </ul>
        <h4>
          <Trans i18nKey="extendedDescriptions.blackLiterman.optimalTitle" components={{ bold: <b /> }} />
        </h4>
        <p>
          <Trans i18nKey="extendedDescriptions.blackLiterman.blpOptimalDesc" components={{ bold: <b /> }} />
          <Latex>{`w^*`}</Latex>
          <Trans i18nKey="extendedDescriptions.blackLiterman.blpOptimalAfter" components={{ bold: <b /> }} />
        </p>
        <Latex block>{`w^{*} = \\frac{1}{\\lambda} \\Sigma^{-1} E[R]`}</Latex>

        <h3>
          <Trans i18nKey="extendedDescriptions.blackLiterman.advantagesTitle" components={{ bold: <b /> }} />
        </h3>
        <ul>
          <li>
            <Trans i18nKey="extendedDescriptions.blackLiterman.advantages.0" components={{ bold: <b /> }} />
          </li>
          <li>
            <Trans i18nKey="extendedDescriptions.blackLiterman.advantages.1" components={{ bold: <b /> }} />
          </li>
          <li>
            <Trans i18nKey="extendedDescriptions.blackLiterman.advantages.2" components={{ bold: <b /> }} />
          </li>
        </ul>
      </div>
    )
  };

  return extendedDescriptions;
};

export default useExtendedDescriptions;
